/**
 * GPS coordinated-turn lean and the leaning-vehicle physics gate.
 *
 * Shared by the sensor-fusion LEAN calculator (which uses the gate to decide
 * whether the GPS coordinated lean may serve as its complementary reference)
 * and the sensor-fusion ACCELERATION calculator (which feeds the coordinated
 * lean into its yaw→pitch decoupling term). One implementation — the two
 * consumers must agree on what "a leaning vehicle" is.
 */

import { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import { AccelerometerReading } from '../domain/telemetry/raw/AccelerometerReading'
import { COORDINATED_LEAN_HEADING_HALF_WINDOW_MS } from '../analysis/config/gpsCoordinatedLean'

/**
 * Unit: deg. Clamp for the coordinated lean. During a GPS dropout the
 * heading is garbage and can imply ±85°+ "lean"; the clamp (mirroring the
 * lean calculator's ±65° physical clamp) keeps sin/cos of garbage bounded.
 */
const COORDINATED_LEAN_CLAMP_DEG = 65

/**
 * Unit: km/h. Below this the heading-rate lean is dominated by position
 * jitter — the series is undefined (NaN) and consumers fall back.
 */
const COORDINATED_LEAN_MIN_SPEED_KMH = 15

/**
 * Leaning-vehicle physics gate. In an upright vehicle (car / go-kart) the
 * body does not bank: the lateral accelerometer channel carries the FULL
 * centripetal acceleration (ay ≈ v·ω), while a coordinated motorcycle reads
 * ay ≈ 0 (the net specific force stays along chassis-z — that is what lets
 * the bike balance). The ratio of calibrated ay to the GPS-derived lateral
 * acceleration therefore separates the two regimes with a wide margin
 * (measured ≈0 on motorcycle session-143033; physics puts an upright
 * vehicle at ≈1). The gate uses only calibrated raw accelerometer data and
 * GPS — never the fused lean (no self-reference).
 */
const UPRIGHT_GATE_MIN_SPEED_KMH = 30
/** Unit: m/s². Minimum GPS lateral acceleration for a sample to inform the upright gate. */
const UPRIGHT_GATE_MIN_LATERAL_MS2 = 2
/** Unit: samples. Minimum qualifying samples before the gate classifies at all. */
const UPRIGHT_GATE_MIN_SAMPLES = 25
/** Unit: ratio. |Σ ay·aLat / Σ aLat²| at or above this classifies the session as upright. */
const UPRIGHT_GATE_LATERAL_RATIO = 0.5
/**
 * Unit: samples. Upright classification additionally requires at least this
 * many qualifying samples in EACH turn direction. Guards against a
 * mount-roll-miscalibrated bike on a predominantly one-direction session: a
 * constant lateral bias correlates with one-signed aLat and could push the
 * ratio past the threshold. Only alternating-direction data can prove ay
 * TRACKS v·ω rather than merely offsetting it. Cost: genuinely upright
 * one-direction sessions (ovals, carousels) classify as "unknown" and keep
 * the consumers' pre-existing behavior.
 */
const UPRIGHT_GATE_MIN_SIDE_SAMPLES = 10

/**
 * Session-level lean regime:
 * - 'leaning'  — coordinated (banking) vehicle: ay stays ≈0 through corners;
 * - 'upright'  — car-like: ay tracks the GPS lateral acceleration;
 * - 'unknown'  — too little cornering data, or all cornering in one
 *   direction (indistinguishable from a lateral-miscalibrated bike).
 */
export type VehicleLeanRegime = 'leaning' | 'upright' | 'unknown'

function findClosestReading<T extends { timestamp: Date }>(
  readings: T[],
  targetTime: Date,
  lastIndex: number = 0
): { reading: T | undefined; index: number } {
  if (readings.length === 0) return { reading: undefined, index: 0 }
  let closest = readings[lastIndex]
  let closestIndex = lastIndex
  let minDiff = Math.abs(
    readings[lastIndex].timestamp.getTime() - targetTime.getTime()
  )
  for (let i = lastIndex + 1; i < readings.length; i++) {
    const diff = Math.abs(
      readings[i].timestamp.getTime() - targetTime.getTime()
    )
    if (diff < minDiff) {
      minDiff = diff
      closest = readings[i]
      closestIndex = i
    } else if (diff > minDiff) {
      break
    }
  }
  return { reading: closest, index: closestIndex }
}

/**
 * Per-GPS-point coordinated-turn lean −atan(v·ω/g) (deg), from the GPS
 * heading rate smoothed over ±COORDINATED_LEAN_HEADING_HALF_WINDOW_MS.
 * NaN where undefined (low speed, degenerate heading, stream edges).
 *
 * Sign convention: heading here increases clockwise from north, so a right
 * turn (ω > 0) yields a NEGATIVE lean per the codebase leanAngle > 0 = LEFT
 * convention (the negation of aerospace roll).
 */
export function computeGpsCoordinatedLeanSeries(
  gpsPoints: GpsPoint[]
): number[] {
  const n = gpsPoints.length
  const headingDeg: number[] = new Array(n).fill(NaN)
  for (let i = 1; i < n; i++) {
    const dLat = gpsPoints[i].latitude - gpsPoints[i - 1].latitude
    const dLon =
      (gpsPoints[i].longitude - gpsPoints[i - 1].longitude) *
      Math.cos((gpsPoints[i].latitude * Math.PI) / 180)
    headingDeg[i] =
      dLat === 0 && dLon === 0
        ? headingDeg[i - 1]
        : (Math.atan2(dLon, dLat) * 180) / Math.PI
  }

  const series: number[] = new Array(n).fill(NaN)
  let a = 0
  let b = 0
  for (let i = 0; i < n; i++) {
    if (gpsPoints[i].speedKmh < COORDINATED_LEAN_MIN_SPEED_KMH) continue
    const startMs =
      gpsPoints[i].timestamp.getTime() - COORDINATED_LEAN_HEADING_HALF_WINDOW_MS
    const endMs =
      gpsPoints[i].timestamp.getTime() + COORDINATED_LEAN_HEADING_HALF_WINDOW_MS
    while (
      a < i &&
      Math.abs(gpsPoints[a + 1].timestamp.getTime() - startMs) <
        Math.abs(gpsPoints[a].timestamp.getTime() - startMs)
    )
      a++
    while (
      b < n - 1 &&
      Math.abs(gpsPoints[b + 1].timestamp.getTime() - endMs) <=
        Math.abs(gpsPoints[b].timestamp.getTime() - endMs)
    )
      b++
    const from = i > 0 ? Math.min(a, i - 1) : a
    const to = i < n - 1 ? Math.max(b, i + 1) : b
    if (from >= to || isNaN(headingDeg[from]) || isNaN(headingDeg[to])) continue
    let dH = headingDeg[to] - headingDeg[from]
    while (dH > 180) dH -= 360
    while (dH < -180) dH += 360
    const dT =
      (gpsPoints[to].timestamp.getTime() -
        gpsPoints[from].timestamp.getTime()) /
      1000
    if (dT <= 0) continue
    const omegaRadS = ((dH / dT) * Math.PI) / 180
    const speedMs = gpsPoints[i].speedKmh / 3.6
    const leanDeg = -(Math.atan((speedMs * omegaRadS) / 9.81) * 180) / Math.PI
    series[i] = Math.max(
      -COORDINATED_LEAN_CLAMP_DEG,
      Math.min(COORDINATED_LEAN_CLAMP_DEG, leanDeg)
    )
  }
  return series
}

/**
 * Classify the session's lean regime from the calibrated lateral channel
 * versus the GPS lateral acceleration (regression slope over
 * clear-cornering samples). See the UPRIGHT_GATE_* constants for the
 * physics and the one-direction hazard.
 *
 * @param accelerometer - CALIBRATED accelerometer stream (bike frame)
 * @param coordinatedLean - output of computeGpsCoordinatedLeanSeries
 */
export function classifyVehicleLeanRegime(
  gpsPoints: GpsPoint[],
  accelerometer: AccelerometerReading[],
  coordinatedLean: number[]
): VehicleLeanRegime {
  let num = 0
  let den = 0
  let count = 0
  let leftCount = 0
  let rightCount = 0
  let accelIndex = 0
  for (let i = 0; i < gpsPoints.length; i++) {
    if (gpsPoints[i].speedKmh < UPRIGHT_GATE_MIN_SPEED_KMH) continue
    if (!Number.isFinite(coordinatedLean[i])) continue
    // v·ω recovered from the coordinated lean: aLat = −g·tan(lean).
    const aLat = -9.81 * Math.tan((coordinatedLean[i] * Math.PI) / 180)
    if (Math.abs(aLat) < UPRIGHT_GATE_MIN_LATERAL_MS2) continue
    const accelResult = findClosestReading(
      accelerometer,
      gpsPoints[i].timestamp,
      accelIndex
    )
    accelIndex = accelResult.index
    const reading = accelResult.reading
    if (!reading) continue
    const factor = reading.unit === 'g' ? 9.81 : 1
    num += reading.y * factor * aLat
    den += aLat * aLat
    count++
    if (aLat > 0) rightCount++
    else leftCount++
  }
  if (count < UPRIGHT_GATE_MIN_SAMPLES || den <= 0) return 'unknown'
  if (Math.abs(num / den) >= UPRIGHT_GATE_LATERAL_RATIO) {
    const bothDirections =
      leftCount >= UPRIGHT_GATE_MIN_SIDE_SAMPLES &&
      rightCount >= UPRIGHT_GATE_MIN_SIDE_SAMPLES
    // One-direction session whose ay tracks (or merely offsets like) v·ω:
    // cannot separate an upright vehicle from a lateral-miscalibrated bike —
    // decline to classify rather than risk misdirecting a bike's consumers.
    return bothDirections ? 'upright' : 'unknown'
  }
  return 'leaning'
}
